import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import * as React from 'react';
import cn from 'classnames';
import Fuse from 'fuse.js';
import { clamp, xorBy, isUndefined } from 'lodash-es';
import { IconCaretDown } from '../../../../icons';
import { Tag } from '../../../tag';
import { Popover } from '../../../popover';
import { OptionList, OptionItem, selectClasses } from '../../select';
import { isFunction, safeInvoke, keys, intentClass, controlClasses, elementIsOrContains, isTruthy, isDefined, ControlAppearanceType, Intent, ControlShapeType, ControlDensityType, ControlSizeType } from '../../../../library';
import { Close } from '../../../close';
import { Pane } from '../../../pane';
var NONE = -1;
var tagInputClasses = {
  MAIN: 'tag-input',
  TAG_INPUT: 'tag-input__input',
  TAG_INPUT_VALUES: 'tag-input__values'
};
export var TagInput = function TagInput(props) {
  var _React$useState = React.useState(NONE),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    activeIndex = _React$useState2[0],
    setActiveIndex = _React$useState2[1];
  var _React$useState3 = React.useState(''),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    inputValue = _React$useState4[0],
    setInputValue = _React$useState4[1];
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    isInputFocused = _React$useState6[0],
    setIsInputFocused = _React$useState6[1];
  var _React$useState7 = React.useState(false),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    showSuggest = _React$useState8[0],
    setShowSuggest = _React$useState8[1];
  var _React$useState9 = React.useState(),
    _React$useState10 = _slicedToArray(_React$useState9, 2),
    fuse = _React$useState10[0],
    setFuse = _React$useState10[1];
  var _React$useState11 = React.useState(null),
    _React$useState12 = _slicedToArray(_React$useState11, 2),
    targetRef = _React$useState12[0],
    setTargetRef = _React$useState12[1];
  var _React$useState13 = React.useState(null),
    _React$useState14 = _slicedToArray(_React$useState13, 2),
    popupRef = _React$useState14[0],
    setPopupRef = _React$useState14[1];
  var _React$useState15 = React.useState(0),
    _React$useState16 = _slicedToArray(_React$useState15, 2),
    width = _React$useState16[0],
    setWidth = _React$useState16[1];
  var inputRef = React.useRef(null);
  var optionListRef = React.useRef(null);
  var isValidIndex = function isValidIndex(index) {
    return index !== NONE && index < props.values.length;
  };
  var getNextActiveIndex = function getNextActiveIndex(direction) {
    if (activeIndex === NONE) {
      return direction < 0 ? findNextIndex(props.values.length, -1) : NONE;
    } else {
      return findNextIndex(activeIndex, direction);
    }
  };
  var findNextIndex = function findNextIndex(startIndex, direction) {
    var values = props.values;
    var index = startIndex + direction;
    while (index > 0 && index < values.length && !values[index]) {
      index += direction;
    }
    return clamp(index, 0, values.length);
  };
  var derivedInputValue = React.useMemo(function () {
    return isUndefined(props.inputValue) ? inputValue : props.inputValue;
  }, [props.inputValue, inputValue]);
  var getValues = function getValues(inputValue) {
    return (props.separator === false ? [inputValue] : inputValue.split(props.separator)).map(function (val) {
      return val.trim();
    }).filter(function (val) {
      return val.length > 0;
    });
  };
  React.useEffect(function () {
    if (typeof props.renderTag === 'string') {
      setFuse(new Fuse(props.options, {
        keys: [props.renderTag],
        includeMatches: false,
        includeScore: false
      }));
    }
  }, [props.options, props.renderTag]);
  var addTag = function addTag(value) {
    var onAdd = props.onAdd,
      onUpdate = props.onUpdate,
      values = props.values;
    if (!isFunction(onAdd)) return;
    var newValues = getValues(value);
    var newTags = newValues.map(function (value, index) {
      return onAdd(value, index);
    }).filter(isTruthy);
    setInputValue('');
    setShowSuggest(false);
    safeInvoke(onUpdate, [].concat(_toConsumableArray(values), _toConsumableArray(newTags)));
  };
  var removeIndexFromValues = function removeIndexFromValues(index) {
    var onRemove = props.onRemove,
      onUpdate = props.onUpdate,
      values = props.values;
    var valueCopy = _toConsumableArray(values);
    var removedOption = valueCopy.splice(index, 1);
    safeInvoke(onUpdate, valueCopy);
    safeInvoke(onRemove, removedOption[0], index);
  };
  var invokeKeyPressCallback = function invokeKeyPressCallback(propCallbackName, event, activeIndex) {
    safeInvoke(props[propCallbackName], event, activeIndex === NONE ? undefined : activeIndex);
  };
  var clearValues = function clearValues() {
    var _inputRef$current;
    safeInvoke(props.onUpdate, []);
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
  };
  var handleContainerFocus = function handleContainerFocus(event) {
    var _inputRef$current2;
    (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.focus();
  };
  var handleClearFocus = function handleClearFocus(e) {
    e.stopPropagation();
  };
  var handleContainerBlur = function handleContainerBlur(e) {
    requestAnimationFrame(function () {
      var isWithinComponent = elementIsOrContains(targetRef, document.activeElement) || elementIsOrContains(popupRef, document.activeElement);
      if (inputRef.current != null && !isWithinComponent) {
        if (props.addOnBlur && inputValue !== undefined && inputValue.length > 0) {
          addTag(inputValue);
        }
        setActiveIndex(NONE);
        setIsInputFocused(false);
        setInputValue('');
        setShowSuggest(false);
        safeInvoke(props.onBlur, e);
      }
    });
  };
  var handleInputFocus = function handleInputFocus(event) {
    setIsInputFocused(true);
    safeInvoke(props.onFocus, event);
  };
  var handleInputChange = function handleInputChange(event) {
    if (!!optionListRef.current) {
      optionListRef.current.setFocusOption(null);
    }
    setActiveIndex(NONE);
    setInputValue(event.currentTarget.value);
    setShowSuggest(!!event.currentTarget.value);
    safeInvoke(props.onInputChange, event);
  };
  var handleInputKeyDown = function handleInputKeyDown(event) {
    var _event$currentTarget = event.currentTarget,
      selectionEnd = _event$currentTarget.selectionEnd,
      value = _event$currentTarget.value;
    var optionList = optionListRef.current;
    var activeIndexToEmit = activeIndex;
    if (event.which === keys.ENTER) {
      if (!!optionList && !!optionList.state.focusedOption) {
        optionList.handleKeyDown(event);
      } else if (value.length > 0) {
        addTag(value);
      } else {
        setShowSuggest(true);
      }
    } else if (event.which === keys.ARROW_DOWN) {
      setShowSuggest(true);
      if (optionList) {
        optionList.handleKeyDown(event);
      }
    } else if (event.which === keys.ARROW_UP) {
      if (optionList) {
        if (optionList.getFocusedIndex() === 0) {
          event.preventDefault();
          optionList.setFocusOption(null);
        } else {
          optionList.handleKeyDown(event);
        }
      }
    } else if (event.which === keys.ESCAPE) {
      setShowSuggest(false);
    } else if (selectionEnd === 0 && props.values.length > 0) {
      if (event.which === keys.ARROW_LEFT || event.which === keys.ARROW_RIGHT) {
        var nextActiveIndex = getNextActiveIndex(event.which === keys.ARROW_RIGHT ? 1 : -1);
        if (nextActiveIndex !== activeIndex) {
          event.stopPropagation();
          activeIndexToEmit = nextActiveIndex;
          setActiveIndex(nextActiveIndex);
        }
      } else if (event.which === keys.BACKSPACE) {
        handleBackspaceToRemove(event);
      }
    }
    invokeKeyPressCallback('onKeyDown', event, activeIndexToEmit);
  };
  var handleInputKeyUp = function handleInputKeyUp(event) {
    invokeKeyPressCallback('onKeyUp', event, activeIndex);
  };
  var handleOptionSelect = function handleOptionSelect(option, e) {
    var _inputRef$current3;
    var activeOptions = xorBy(props.values, [option], props.idProp);
    (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 || _inputRef$current3.focus();
    setInputValue('');
    setShowSuggest(false);
    safeInvoke(props.onUpdate, activeOptions);
  };
  var handleBackspaceToRemove = function handleBackspaceToRemove(event) {
    var previousActiveIndex = activeIndex;
    setActiveIndex(getNextActiveIndex(-1));
    if (isValidIndex(previousActiveIndex)) {
      event.stopPropagation();
      removeIndexFromValues(previousActiveIndex);
    }
  };
  var handleDropdownClick = function handleDropdownClick() {
    setShowSuggest(true);
  };
  var handlePopoverOpening = function handlePopoverOpening() {
    if (!!targetRef) {
      setWidth(targetRef.getBoundingClientRect().width);
    }
  };
  var maybeRenderTag = function maybeRenderTag(value, index) {
    if (isUndefined(value)) return null;
    var tagProps = isFunction(props.tagProps) ? props.tagProps(value, index) : props.tagProps;
    var tagLabel = !isFunction(props.renderTag) ? value[props.renderTag] : props.renderTag(value);
    return React.createElement(Tag, _extends({
      key: 'tag__' + value[props.idProp] + '__' + index,
      intent: index === activeIndex ? 'primary' : 'none',
      density: props.density,
      onDismiss: props.isDisabled ? undefined : function () {
        return removeIndexFromValues(index);
      },
      size: props.size,
      shape: props.shape
    }, tagProps), tagLabel);
  };
  var renderTagOption = function renderTagOption(option, itemProps) {
    var tagLabel = isFunction(props.renderTag) ? props.renderTag(option) : option[props.renderTag];
    return React.createElement(OptionItem, {
      key: itemProps.id,
      id: itemProps.id,
      isActive: itemProps.modifiers.active,
      isDisabled: itemProps.modifiers.disabled,
      isFocused: itemProps.modifiers.focused,
      onClick: itemProps.handleClick,
      onMouseOver: itemProps.handleMouseEnter
    }, tagLabel);
  };
  var renderNoOptions = function renderNoOptions() {
    return React.createElement(Pane, {
      padding: [4],
      style: {
        minWidth: width
      },
      className: "t--center"
    }, "Press Enter to Create \xA0", getValues(derivedInputValue).map(function (val, index) {
      return React.createElement("strong", {
        key: index
      }, "\"", val, "\"");
    }).reduce(function (accu, cur, index) {
      return accu.length === 0 ? [cur] : [].concat(_toConsumableArray(accu), [React.createElement("span", {
        key: index + '_pad'
      }, "\xA0+\xA0"), cur]);
    }, []));
  };
  var RenderSuggest = function RenderSuggest() {
    var suggestList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    return React.createElement(OptionList, {
      id: "".concat(props.field, "__list"),
      options: suggestList,
      renderOption: renderTagOption,
      activeOptions: props.values,
      onOptionSelect: handleOptionSelect,
      ref: optionListRef,
      optionIdProp: props.idProp,
      noResults: renderNoOptions(),
      style: {
        minWidth: width
      }
    });
  };
  var renderClose = function renderClose() {
    if (!props.values || props.values.length === 0 || props.isDisabled) {
      return null;
    }
    return React.createElement("div", {
      className: selectClasses.ADDON
    }, React.createElement(Close, {
      shape: props.shape,
      appearance: "fill",
      size: props.size,
      onClick: clearValues,
      onFocus: handleClearFocus
    }));
  };
  var filteredSuggest = isDefined(fuse) && !!derivedInputValue ? fuse.search(derivedInputValue) : props.options;
  var classNames = cn(tagInputClasses.MAIN, '-multiline', intentClass(props.intent), controlClasses({
    size: props.size,
    appearance: props.appearance,
    shape: props.shape,
    density: props.density
  }), {
    '-focus': isInputFocused,
    '-disabled': props.isDisabled
  }, props.className);
  var resolvedPlaceholder = props.values.length === 0 ? props.placeholder : undefined;
  return React.createElement(Popover, {
    autoFocus: false,
    isOpen: showSuggest,
    enforceFocus: false,
    className: "select__options",
    content: RenderSuggest(filteredSuggest),
    position: "bottom",
    isMinimal: true,
    isDisabled: props.isDisabled,
    boundary: props.boundary,
    onOpening: handlePopoverOpening,
    onResize: handlePopoverOpening,
    getReferenceRef: setTargetRef,
    getPopupRef: setPopupRef,
    modifiers: [{
      name: 'offset',
      options: {
        offset: [0, 0]
      }
    }]
  }, React.createElement("div", {
    className: classNames,
    tabIndex: 0,
    onFocus: handleContainerFocus,
    onBlur: handleContainerBlur
  }, React.createElement("div", {
    className: tagInputClasses.TAG_INPUT_VALUES
  }, props.values.map(maybeRenderTag), React.createElement("input", {
    type: "text",
    value: derivedInputValue,
    onFocus: handleInputFocus,
    onChange: handleInputChange,
    onKeyDown: handleInputKeyDown,
    onKeyUp: handleInputKeyUp,
    placeholder: resolvedPlaceholder,
    className: cn('control__text'),
    disabled: props.isDisabled,
    ref: inputRef
  })), React.createElement("span", {
    className: cn(selectClasses.ADDON, selectClasses.ACTION),
    onClick: handleDropdownClick
  }, React.createElement(IconCaretDown, null)), renderClose()));
};
TagInput.defaultProps = {
  appearance: ControlAppearanceType.OUTLINE,
  boundary: 'viewport',
  density: ControlDensityType.NORMAL,
  intent: Intent.NONE,
  isDisabled: false,
  separator: ',',
  shape: ControlShapeType.ROUNDED,
  size: ControlSizeType.MEDIUM,
  tagProps: {},
  values: []
};